import { useState } from "react";
import { Archive, Building2, ChevronDown, Palette, Search, UsersRound } from "lucide-react";
import { Button, Checkbox, cn, Popover, PopoverContent, PopoverTrigger } from "@nexus/ui";
import { useTranslation } from "react-i18next";
import { translateBrandColorLabel } from "@/features/identity-hub/lib/brand-colors";
import { FilterSelectChip } from "./shared/toolbar/FilterSelectChip";
import { ToggleChip } from "./shared/toolbar/ToggleChip";
import { ClearFilters } from "./shared/toolbar/ClearFilters";

export interface OrgFilters {
  search: string;
  size: "large" | "medium" | "small" | null;
  /** When true, archived (soft-deleted) orgs are mixed into the grid. */
  showArchived: boolean;
  /** Brand-color hex values the user explicitly hid. Empty = show all ("Select All"). */
  excludedColors: string[];
}

interface OrgToolbarProps {
  filters: OrgFilters;
  onFilterChange: <K extends keyof OrgFilters>(key: K, value: OrgFilters[K]) => void;
  /** Distinct accent colors for the filter menu (palette + custom hex in use). */
  availableColors: ReadonlyArray<{ value: string; label: string }>;
  onNewOrg: () => void;
  canCreate?: boolean;
  /** Count of archived orgs hidden behind the "Show archived" toggle (for the badge). */
  archivedCount?: number;
}

export function excludedColorsAfterSelectAllToggle(
  checked: boolean | "indeterminate",
  availableColors: ReadonlyArray<{ value: string }>,
): string[] {
  if (checked === false) {
    return availableColors.map((c) => c.value);
  }
  return [];
}

export function OrgToolbar({ filters, onFilterChange, availableColors, onNewOrg, canCreate = true, archivedCount = 0 }: OrgToolbarProps) {
  const { t } = useTranslation();
  const tb = (key: string, opts?: Record<string, unknown>) => t(`iam.dashboard.orgs.toolbar.${key}`, opts);

  const SIZE_OPTIONS = [
    { value: "large",  label: tb("sizeLarge")  },
    { value: "medium", label: tb("sizeMedium") },
    { value: "small",  label: tb("sizeSmall")  },
  ] as const;

  const availableColorValues = new Set(availableColors.map((c) => c.value));
  const activeExcludedColors = filters.excludedColors.filter((c) => availableColorValues.has(c));
  const colorFilterActive = activeExcludedColors.length > 0;
  const activeFilterCount = [filters.size, colorFilterActive || null].filter(Boolean).length;
  const sizeLabel = SIZE_OPTIONS.find((s) => s.value === filters.size)?.label;
  const [colorPopoverOpen, setColorPopoverOpen] = useState(false);

  const isAllColorsSelected = activeExcludedColors.length === 0;
  const isSomeColorsSelected =
    activeExcludedColors.length > 0 && activeExcludedColors.length < availableColors.length;
  const selectAllChecked: boolean | "indeterminate" = isAllColorsSelected
    ? true
    : isSomeColorsSelected
      ? "indeterminate"
      : false;
  const visibleCount = availableColors.length - activeExcludedColors.length;
  const singleVisibleColor = availableColors.find((c) => !activeExcludedColors.includes(c.value));
  const colorLabel = isAllColorsSelected
    ? tb("colorFilterLabel")
    : visibleCount === 1
      ? singleVisibleColor
        ? translateBrandColorLabel(t, singleVisibleColor.value, singleVisibleColor.label)
        : tb("colorCount", { count: 1 })
      : tb("colorCount", { count: visibleCount });

  const toggleColor = (hex: string, checked: boolean | "indeterminate") => {
    if (checked === true) {
      onFilterChange("excludedColors", filters.excludedColors.filter((c) => c !== hex));
      return;
    }
    const next = [...activeExcludedColors, hex];
    if (next.length >= availableColors.length) return;
    onFilterChange("excludedColors", next);
  };

  const toggleSelectAll = (checked: boolean | "indeterminate") => {
    onFilterChange("excludedColors", excludedColorsAfterSelectAllToggle(checked, availableColors));
  };

  const isColorChecked = (hex: string) => !activeExcludedColors.includes(hex);

  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 px-3 py-3 sm:px-6">
      {/* Search — fills the row next to the action on mobile; fixed-width on sm+ */}
      <label className="relative order-1 flex h-8 min-w-0 flex-1 items-center gap-2 rounded-md border border-border bg-background px-2.5 text-sm text-muted-foreground focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/40 sm:flex-none">
        <Search className="h-3.5 w-3.5 shrink-0" />
        <input
          type="search"
          placeholder={tb("searchPlaceholder")}
          value={filters.search}
          onChange={(e) => onFilterChange("search", e.target.value)}
          className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/70 sm:w-52 sm:flex-none"
        />
      </label>

      {/* Filter chips — own full-width row on mobile; inline on sm+ */}
      <div className="order-3 flex w-full flex-wrap items-center gap-1.5 sm:order-2 sm:w-auto sm:flex-1">
        {/* Size */}
        <FilterSelectChip
          icon={<UsersRound className="h-3 w-3 shrink-0" />}
          label={sizeLabel ?? tb("filterSize")}
          active={!!filters.size}
          value={filters.size ?? ""}
          options={[
            { value: "", label: tb("filterSizeAny") },
            ...SIZE_OPTIONS.map((s) => ({ value: s.value, label: s.label })),
          ]}
          onValueChange={(v) => onFilterChange("size", (v || null) as OrgFilters["size"])}
        />

        {/* Color — palette + custom hex in use */}
        {availableColors.length > 0 && (
          <Popover open={colorPopoverOpen} onOpenChange={setColorPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  "flex cursor-pointer select-none items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                  colorFilterActive
                    ? "border-primary/40 bg-primary/10 text-primary"
                    : "border-border bg-transparent text-muted-foreground hover:border-foreground/30 hover:text-foreground",
                )}
              >
                <Palette className="h-3 w-3 shrink-0" />
                <span className="max-w-[120px] truncate">{colorLabel}</span>
                <ChevronDown className="h-2.5 w-2.5 shrink-0 opacity-60" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-48 max-h-64 overflow-y-auto p-1.5" onCloseAutoFocus={(e) => e.preventDefault()}>
              {/* Select All */}
              <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-muted">
                <Checkbox
                  checked={selectAllChecked}
                  onCheckedChange={toggleSelectAll}
                />
                <span className="font-medium">{tb("selectAllColors")}</span>
              </label>
              <div className="my-1 border-t border-border" />
              {availableColors.map((color) => (
                <label key={color.value} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-muted">
                  <Checkbox
                    checked={isColorChecked(color.value)}
                    onCheckedChange={(checked) => toggleColor(color.value, checked)}
                  />
                  <span
                    className="inline-block h-3 w-3 shrink-0 rounded-full border border-border/50"
                    style={{ backgroundColor: color.value }}
                    aria-hidden
                  />
                  <span>{translateBrandColorLabel(t, color.value, color.label)}</span>
                </label>
              ))}
            </PopoverContent>
          </Popover>
        )}

        {/* Archived toggle — pressed = archived rows mixed into the grid */}
        <ToggleChip
          icon={<Archive className="h-3 w-3 shrink-0" />}
          label={tb("showArchived")}
          active={filters.showArchived}
          onClick={() => onFilterChange("showArchived", !filters.showArchived)}
          count={archivedCount}
          title={filters.showArchived ? tb("hideArchivedTooltip") : tb("showArchivedTooltip")}
        />

        {activeFilterCount > 0 && (
          <ClearFilters
            label={tb("clearFilters")}
            onClick={() => {
              onFilterChange("size", null);
              onFilterChange("excludedColors", []);
            }}
          />
        )}
      </div>

      {/* Primary action — stays on the search row on mobile; at the end on sm+ */}
      {canCreate && (
        <div className="order-2 shrink-0 sm:order-3 sm:ml-auto">
          <Button variant="default" size="sm" className="gap-1.5" onClick={onNewOrg}>
            <Building2 className="h-3.5 w-3.5" />
            {tb("newOrganization")}
          </Button>
        </div>
      )}
    </div>
  );
}
